import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

const AUTH_BASE_URL = "https://api-dev.silware.com.au/api";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

interface User {
  id: number;
  username: string;
  email?: string;
  date_joined?: string;
  is_admin?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, tenantId: string) => Promise<boolean>;
  logout: () => Promise<void>;
  handleUnauthorized: () => void;
  token: string | null;
  tenantId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(
    localStorage.getItem("tenantId"),
  );
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserInfo = async (authToken: string, tid: string): Promise<User | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
          ...(tid ? { "X-Tenant-Id": tid } : {}),
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.status && responseData.data) {
          return responseData.data;
        } else {
          console.error("Invalid user info response:", responseData);
          return null;
        }
      } else {
        console.error("Failed to fetch user info:", response.status);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      return null;
    }
  };

  useEffect(() => {
    const validateAndLoadUser = async () => {
      const savedToken = localStorage.getItem("authToken");
      const savedTenantId = localStorage.getItem("tenantId");

      if (savedToken) {
        // Validate token by fetching current user info
        const userData = await fetchUserInfo(savedToken, savedTenantId || "");

        if (userData) {
          setToken(savedToken);
          setUser(userData);
          if (savedTenantId) setTenantId(savedTenantId);
          localStorage.setItem("user", JSON.stringify(userData));
        } else {
          // Token is invalid, clear storage
          localStorage.removeItem("authToken");
          localStorage.removeItem("user");
          localStorage.removeItem("tenantId");
        }
      }

      setIsLoading(false);
    };

    validateAndLoadUser();
  }, []);

  const login = async (email: string, password: string, tid: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      const response = await fetch(`${AUTH_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(tid ? { "X-Tenant-Id": tid } : {}),
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const response_data = await response.json();
        const { token: authToken } = response_data.data;

        if (!authToken) {
          console.error("Missing token in response:", response_data);
          return false;
        }

        // Fetch user info from /api/users/me/
        const userData = await fetchUserInfo(authToken, tid);

        if (!userData) {
          console.error("Failed to fetch user information");
          return false;
        }

        setToken(authToken);
        setUser(userData);

        localStorage.setItem("authToken", authToken);
        localStorage.setItem("user", JSON.stringify(userData));

        if (tid) {
          setTenantId(tid);
          localStorage.setItem("tenantId", tid);
        }

        return true;
      } else {
        const errorData = await response.text();
        console.error("Login failed:", response.status, errorData);
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (authToken) {
        await fetch(`${AUTH_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
          },
        });
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      setUser(null);
      setToken(null);
      setTenantId(null);
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("tenantId");
    }
  };

  const handleUnauthorized = () => {
    // Clear auth state
    setUser(null);
    setToken(null);
    setTenantId(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("tenantId");

    // Redirect to login page
    window.location.href = "/login";
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    handleUnauthorized,
    token,
    tenantId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
